// src/features/mech/sheetmetal/questions/operations.jsx
import React, { useRef, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlButton from "../../../../shared/components/PerlButton";
import PerlSelect from "../../../../shared/components/PerlSelect";

/**
 * OperationsQuestion Component
 *
 * Captures operations count, time per bend, deburring cycle time,
 * number of bends, taps/holes, welding length, and optional reference image upload.
 *
 * @param {object} value - { operationsCount, timePerBend, deburringEnabled, 
 *                            deburringCycleTime, noOfBendsPressBrake, noOfTapsHoles, weldingLength, imageName, imageData }
 * @param {function} onChange - Callback when fields change
 * @param {string} processSelection - Selected process type (Stage, Progressive, Turret, Laser, etc.)
 * @param {function} [onNextStep] - Optional callback to proceed to next step on Enter key
 * @param {number} [currentStep] - Current step index
 */
const DEBURRING_YES_NO_OPTIONS = ["Yes", "No"];

export default function OperationsQuestion({ value = {}, onChange, processSelection = "", onNextStep, currentStep }) {
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
    surface: "#191A1B",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  // Normalize process name for comparison (case-insensitive)
  const normalizedProcess = (processSelection || "").toLowerCase().trim();
  const isTurretOrLaser = normalizedProcess === "turret" || normalizedProcess === "laser" || normalizedProcess.includes("laser cutting");
  const isStageOrProgressive = normalizedProcess === "stage" || normalizedProcess === "progressive";

  // Set default values to 0 when process changes and fields should be hidden
  useEffect(() => {
    if (!onChange) return;

    const updates = {};
    let hasUpdates = false;

    // Set operationsCount to 0 if Turret or Laser cutting (field is hidden)
    if (isTurretOrLaser) {
      if (value?.operationsCount !== "0") {
        updates.operationsCount = "0";
        hasUpdates = true;
      }
    }

    // Set timePerBend, noOfBendsPressBrake, and averageBendingLength to 0 if Stage or Progressive (fields are hidden)
    if (isStageOrProgressive) {
      if (value?.timePerBend !== "0") {
        updates.timePerBend = "0";
        hasUpdates = true;
      }
      if (value?.noOfBendsPressBrake !== "0") {
        updates.noOfBendsPressBrake = "0";
        hasUpdates = true;
      }
      if (value?.averageBendingLength !== "0") {
        updates.averageBendingLength = "0";
        hasUpdates = true;
      }
    }

    // Clear noOfBends if process is not Turret or Laser (field is hidden)
    if (!isTurretOrLaser) {
      if (value?.noOfBends !== "") {
        updates.noOfBends = "";
        hasUpdates = true;
      }
    }

    if (hasUpdates) {
      onChange({
        ...value,
        ...updates,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processSelection]); // Only run when process selection changes

  // Calculate Time per bend based on Average Bending Length
  useEffect(() => {
    if (!onChange) return;
    // Only calculate if "No of Bends" is not "No" and averageBendingLength has a value
    if (value?.noOfBends === "No" || !value?.averageBendingLength) return;
    if (isStageOrProgressive) return; // Don't calculate if fields are hidden

    const avgLength = parseFloat(value.averageBendingLength);
    if (isNaN(avgLength)) return;

    let calculatedTime = "";
    if (avgLength < 200) {
      calculatedTime = "4";
    } else if (avgLength >= 200 && avgLength < 400) {
      calculatedTime = "6";
    } else if (avgLength >= 400 && avgLength < 600) {
      calculatedTime = "8";
    } else if (avgLength >= 600) {
      calculatedTime = "10";
    }

    // Only update if the calculated value is different from current value
    if (calculatedTime && value?.timePerBend !== calculatedTime) {
      onChange({
        ...value,
        timePerBend: calculatedTime,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.averageBendingLength, value?.noOfBends, isStageOrProgressive]);

  const handleFieldChange = (field, fieldValue) => {
    if (!onChange) return;
    onChange({
      operationsCount: value?.operationsCount || "",
      timePerBend: value?.timePerBend || "",
      deburringEnabled: value?.deburringEnabled || "",
      deburringCycleTime: value?.deburringCycleTime || "",
      noOfBendsPressBrake: value?.noOfBendsPressBrake || "",
      averageBendingLength: value?.averageBendingLength || "",
      noOfBends: value?.noOfBends || "",
      noOfTapsHoles: value?.noOfTapsHoles || "",
      weldingLength: value?.weldingLength || "",
      imageName: value?.imageName || "",
      imageData: value?.imageData || "",
      [field]: fieldValue,
      // Clear cycle time if deburring is disabled
      ...(field === "deburringEnabled" && fieldValue !== "Yes" ? { deburringCycleTime: "" } : {}),
      // Clear noOfBendsPressBrake, averageBendingLength and timePerBend if noOfBends is "No"
      ...(field === "noOfBends" && fieldValue !== "Yes" ? { noOfBendsPressBrake: "", averageBendingLength: "", timePerBend: "" } : {}),
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (!onChange) return;
      onChange({
        operationsCount: value?.operationsCount || "",
        timePerBend: value?.timePerBend || "",
        deburringEnabled: value?.deburringEnabled || "",
        deburringCycleTime: value?.deburringCycleTime || "",
        noOfBendsPressBrake: value?.noOfBendsPressBrake || "",
        averageBendingLength: value?.averageBendingLength || "",
        noOfBends: value?.noOfBends || "",
        noOfTapsHoles: value?.noOfTapsHoles || "",
        weldingLength: value?.weldingLength || "",
        imageName: file.name,
        imageData: reader.result,
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PerlLabelMedium sx={{ display: "block" }}>Operations</PerlLabelMedium>

      {/* Hide "No. of Operations" if Process is Turret or Laser cutting */}
      {!isTurretOrLaser && (
        <PerlTextField
          label="No. of Operations"
          placeholder="Enter number of operations"
          value={value?.operationsCount || ""}
          onChange={(e) => handleFieldChange("operationsCount", e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onNextStep && value?.operationsCount && value.operationsCount.toString().trim() !== "") {
              e.preventDefault();
              onNextStep();
            }
          }}
          fullWidth
        />
      )}

      <Box>
        <PerlLabelMedium sx={{ mb: 1, display: "block" }}>
          Deburring [Cycle time (sec)]
        </PerlLabelMedium>
        <PerlSelect
          label=""
          value={value?.deburringEnabled || ""}
          onChange={(e) => handleFieldChange("deburringEnabled", e.target.value)}
          placeholder="Select Yes or No"
          options={DEBURRING_YES_NO_OPTIONS}
          showLabel={false}
          fullWidth
          sx={{
            mb: value?.deburringEnabled === "Yes" ? 2 : 0,
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": {
              borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: `${alpha(brandColor, 0.8)} !important`,
            },
            "&.Mui-focused": {
              "& fieldset": {
                borderColor: `${brandColor} !important`,
                borderWidth: "2px !important",
              },
            },
            "&.Mui-focused fieldset": {
              borderColor: `${brandColor} !important`,
              borderWidth: "2px !important",
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${brandColor} !important`,
              borderWidth: "2px !important",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
              },
              "&:hover fieldset": {
                borderColor: `${alpha(brandColor, 0.8)} !important`,
              },
              "&.Mui-focused fieldset": {
                borderColor: `${brandColor} !important`,
                borderWidth: "2px !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${brandColor} !important`,
                borderWidth: "2px !important",
              },
            },
            // Override any primary color usage
            "&.Mui-focused.MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: `${brandColor} !important`,
              },
            },
            "& .MuiSelect-select": {
              color: !value?.deburringEnabled
                ? alpha(perlColors.textSecondary, 0.7)
                : perlColors.textPrimary,
              fontSize: "0.9rem",
            },
            "& .MuiSvgIcon-root": {
              color: perlColors.textSecondary,
            },
          }}
        />
      </Box>

      {/* Show "No of Bends" only if Process is Turret or Laser */}
      {isTurretOrLaser && (
        <Box>
          <PerlLabelMedium sx={{ mb: 1, display: "block" }}>
            No of Bends
          </PerlLabelMedium>
          <PerlSelect
            label=""
            value={value?.noOfBends || ""}
            onChange={(e) => handleFieldChange("noOfBends", e.target.value)}
            placeholder="Select Yes or No"
            options={DEBURRING_YES_NO_OPTIONS}
            showLabel={false}
            fullWidth
            sx={{
              bgcolor: alpha(perlColors.white, 0.02),
              color: perlColors.textPrimary,
              fontSize: "0.9rem",
              borderRadius: 1.5,
              "& fieldset": {
                borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: `${alpha(brandColor, 0.8)} !important`,
              },
              "&.Mui-focused": {
                "& fieldset": {
                  borderColor: `${brandColor} !important`,
                  borderWidth: "2px !important",
                },
              },
              "&.Mui-focused fieldset": {
                borderColor: `${brandColor} !important`,
                borderWidth: "2px !important",
                boxShadow: `0 0 0 2px ${brandColor}33`,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${brandColor} !important`,
                borderWidth: "2px !important",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: `${alpha(perlColors.textPrimary, 0.12)} !important`,
                },
                "&:hover fieldset": {
                  borderColor: `${alpha(brandColor, 0.8)} !important`,
                },
                "&.Mui-focused fieldset": {
                  borderColor: `${brandColor} !important`,
                  borderWidth: "2px !important",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${brandColor} !important`,
                  borderWidth: "2px !important",
                },
              },
              "&.Mui-focused.MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: `${brandColor} !important`,
                },
              },
              "& .MuiSelect-select": {
                color: !value?.noOfBends
                  ? alpha(perlColors.textSecondary, 0.7)
                  : perlColors.textPrimary,
                fontSize: "0.9rem",
              },
              "& .MuiSvgIcon-root": {
                color: perlColors.textSecondary,
              },
            }}
          />
        </Box>
      )}

      {/* Hide "NoOfBends_PressBrake" if Process is Stage or Progressive */}
      {!isStageOrProgressive && (
        <PerlTextField
          label="NoOfBends_PressBrake"
          placeholder="Enter number of bends for press brake"
          value={value?.noOfBendsPressBrake || ""}
          onChange={(e) => handleFieldChange("noOfBendsPressBrake", e.target.value)}
          fullWidth
          type="number"
          disabled={value?.noOfBends === "No"}
        />
      )}

      {/* Hide "Average Bending Length" if Process is Stage or Progressive */}
      {!isStageOrProgressive && (
        <PerlTextField
          label="Average Bending Length"
          placeholder="Enter average bending length"
          value={value?.averageBendingLength || ""}
          onChange={(e) => handleFieldChange("averageBendingLength", e.target.value)}
          fullWidth
          type="number"
          disabled={value?.noOfBends === "No"}
        />
      )}

      {/* Hide "Time per bend (Sec)" if Process is Stage or Progressive - Show after NoOfBends_PressBrake for Turret/Laser */}
      {!isStageOrProgressive && (
        <PerlTextField
          label="Time per bend (Sec)"
          placeholder="Enter time per bend in seconds"
          value={value?.timePerBend || ""}
          onChange={(e) => handleFieldChange("timePerBend", e.target.value)}
          fullWidth
          type="number"
          disabled={value?.noOfBends === "No"}
        />
      )}

      <PerlTextField
        label="No. of Taps/Holes (Tapping)"
        placeholder="Enter number of taps/holes"
        value={value?.noOfTapsHoles || ""}
        onChange={(e) => handleFieldChange("noOfTapsHoles", e.target.value)}
        fullWidth
        type="number"
      />

      <PerlTextField
        label="Welding Length (mm)"
        placeholder="Enter welding length in mm"
        value={value?.weldingLength || ""}
        onChange={(e) => handleFieldChange("weldingLength", e.target.value)}
        fullWidth
        type="number"
      />

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {/* <PerlButton
        variant="outlined"
        onClick={() => fileInputRef.current?.click()}
        sx={{ alignSelf: "flex-start" }}
      >
        Upload image
      </PerlButton> */}

      {value?.imageName && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Selected: {value.imageName}
        </Typography>
      )}
    </Box>
  );
}

